import type { EvmChainId } from '@/service/EVM/config/types';
import type { UseSwitchChainReturnType } from '@wagmi/vue';
import { readContract, writeContract } from '@wagmi/core';
import type { Address } from 'viem';
import { hexToBigInt, pad } from 'viem';
import { type EvmFullFillOrderType, EvmItemType } from '@/service/EVM/types';
import { isEvmErc1155 } from '@/service/utils';
import { NULL_ADDRESS } from '@/service/EVM/consts';
import EvmProvider, { type EvmProviderInterface } from '@/service/EVM/provider';
import { revenlyAbi } from '~/service/EVM/abi/marketplace';
import type { UserType } from '~/service/types';
import { useProviderConfig } from '#imports';

/**
 * @param config - config for evm provider
 * @param switchNetworkFn - function for network change
 */

export interface EvmNFTBuyerInterface extends EvmProviderInterface {
    switchNetworkFn: UseSwitchChainReturnType['switchChainAsync'];
    user: UserType;
}

export class EvmNFTBuyer extends EvmProvider implements EvmNFTBuyerInterface {
    switchNetworkFn;
    user;

    constructor({
        config,
                    eventEmitter,
                    switchNetworkFn,
                    chainIds,
                    user
    }: EvmNFTBuyerInterface) {
        super({ config, chainIds, eventEmitter });
        this.switchNetworkFn = switchNetworkFn;
        this.user = user;
    }

    private async handleSwitchNetwork(paramChainId: EvmChainId, currentChainId: EvmChainId) {
        if (paramChainId !== currentChainId) {
            try {
                await this.switchNetworkFn({ chainId: paramChainId });
            }
            catch (e) {
                throw new Error('Switching network error');
            }
        }
    }

    private calcSum(data: EvmFullFillOrderType[]) {
        return data.reduce(
            (acc, val) => {
                if (val.order.orderData.parameters.offer[0]) {
                    return isEvmErc1155(val.order.orderData.parameters.offer[0].itemType) && val.amount1155
                        ? ((Number(val.order.value) / Number(val.order.orderData.parameters.offer[0].startAmount)) * val.amount1155) + acc
                        : Number(val.order.value) + acc;
                }
                return 0;
            }, 0
        );
    }

    private async prepareOrders(fullFillOrders: EvmFullFillOrderType[]) {
        return fullFillOrders.map(({ order, amount1155 }) => {
            const numerator = order.orderData.parameters.offer[0] && isEvmErc1155(order.orderData.parameters.offer[0].itemType) && amount1155
                ? amount1155
                : 1;

            const denominator = order.orderData.parameters.offer[0] && isEvmErc1155(order.orderData.parameters.offer[0].itemType) && amount1155
                ? order.orderData.parameters.offer[0].startAmount
                : 1;

            return {
                parameters: {
                    offerer: order.orderData.parameters.offerer,
                    zone: order.orderData.parameters.zone,
                    offer: order.orderData.parameters.offer.map(item => ({
                        itemType: item.itemType,
                        token: item.token.toLowerCase() as Address,
                        identifierOrCriteria: BigInt(item.identifierOrCriteria),
                        startAmount: BigInt(item.startAmount),
                        endAmount: BigInt(item.endAmount)
                    })),
                    consideration: order.orderData.parameters.consideration.map(({
                        recipient,
                        itemType,
                        token,
                        identifierOrCriteria,
                        startAmount,
                        endAmount
                    }) => {
                        return {
                            itemType,
                            token: (token.toLowerCase()) as Address,
                            identifierOrCriteria: BigInt(identifierOrCriteria),
                            startAmount: BigInt(startAmount),
                            endAmount: BigInt(endAmount),
                            recipient: (recipient.toLowerCase()) as Address
                        };
                    }),
                    orderType: order.orderData.parameters.orderType,
                    startTime: BigInt(order.orderData.parameters.startTime),
                    endTime: BigInt(order.orderData.parameters.endTime),
                    zoneHash: pad(order.orderData.parameters.zoneHash),
                    salt: hexToBigInt(order.orderData.parameters.salt),
                    conduitKey: pad(order.orderData.parameters.conduitKey),
                    counter: BigInt(order.orderData.parameters.counter),
                    totalOriginalConsiderationItems: BigInt(order.orderData.parameters.consideration.length)
                },
                numerator: BigInt(numerator),
                denominator: BigInt(denominator),
                signature: order.orderData.signature,
                extraData: ('0x') as Address
            };
        });
    }

    private async _getTokenSymbol(tokenAddress: Address, chainId: EvmChainId): Promise<string> {
        if (tokenAddress === NULL_ADDRESS) {
            const { map: { chainMap } } = useProviderConfig()(['map', 'contract']);
            return (Object.values(chainMap).find(network => network.id === chainId))?.nativeCurrency.symbol || 'ETH';
        }
        try {
            const symbol = await readContract(this.config, {
                address: tokenAddress,
                abi: [{
                    inputs: [],
                    name: 'symbol',
                    outputs: [{ name: '', type: 'string' }],
                    stateMutability: 'view',
                    type: 'function'
                }],
                functionName: 'symbol',
                chainId
            });
            return symbol as string;
        }
        catch (e) {
            console.error('Failed to get token symbol', e);
            return tokenAddress;
        }
    }

    public async buyNft({
        paramChainId,
                            fullFillOrders,
                            marketplaceContractAddress
    }: {
        paramChainId: EvmChainId;
        fullFillOrders: EvmFullFillOrderType[];
        marketplaceContractAddress: Address;
    }) {
        if (!this.user?.address || !this.user?.chainId) {
            throw new Error('Please log in');
        }

        await this.handleSwitchNetwork(paramChainId, this.user.chainId);

        const sum = this.calcSum(fullFillOrders);

        const erc20TokenAddress = fullFillOrders
            .flatMap(item => item.order.orderData.parameters.consideration)
            .find(considerationItem =>
                considerationItem.itemType === EvmItemType.ERC20
            )?.token;

        const { map: { chainMap } } = useProviderConfig()(['map', 'contract']);

        const currency = erc20TokenAddress
            ? await this._getTokenSymbol(erc20TokenAddress, paramChainId)
            : (Object.values(chainMap).find(network => network.id === paramChainId))?.nativeCurrency.symbol;

        await this.eventEmitter.emit('balance', async () => {
            if (!this.user?.address) {
                throw new Error('Please log in');
            }

            const myBalance = await this.getUserBalance({
                userAddress: this.user.address,
                chainId: paramChainId,
                erc20TokenAddress
            });

            if (Number(myBalance) < sum) {
                throw new Error(JSON.stringify({
                    myBalance: myBalance.toString(),
                    needSum: sum.toString(),
                    currency: currency
                }));
            }

            return myBalance;
        });

        const orders = await this.prepareOrders(fullFillOrders);

        const fulfillAvailableOrdersParams = {
            orders,
            criteriaResolvers: [],
            offerFulfillments: orders.map((item, orderIndex) => item.parameters.offer.map((_, offerIndex) => [
                {
                    orderIndex: BigInt(orderIndex),
                    itemIndex: BigInt(offerIndex)
                }
            ])).flat(),
            considerationFulfillments: orders.map((item, orderIndex) => item.parameters.consideration.map((_, considerationIndex) => [
                {
                    orderIndex: BigInt(orderIndex),
                    itemIndex: BigInt(considerationIndex)
                }
            ])).flat(),
            fulfillerConduitKey: pad(NULL_ADDRESS),
            recipient: this.user.address,
            maximumFulfilled: BigInt(100)
        };

        // function customStringify(value: any): string {
        //     return JSON.stringify(value, (_, v) =>
        //         typeof v === 'bigint' ? v.toString() : v
        //     );
        // }
        //
        // console.log(customStringify([
        //     fulfillAvailableOrdersParams.orders,
        //     fulfillAvailableOrdersParams.criteriaResolvers,
        //     fulfillAvailableOrdersParams.offerFulfillments,
        //     fulfillAvailableOrdersParams.considerationFulfillments,
        //     fulfillAvailableOrdersParams.fulfillerConduitKey,
        //     fulfillAvailableOrdersParams.recipient,
        //     fulfillAvailableOrdersParams.maximumFulfilled
        // ]), 'параметры то что покупаем');
        // console.log('за какой контракт', marketplaceContractAddress);

        try {
            const transaction = await writeContract(this.config, {
                address: marketplaceContractAddress,
                abi: revenlyAbi,
                functionName: 'fulfillAvailableAdvancedOrders',
                args: [
                    fulfillAvailableOrdersParams.orders,
                    fulfillAvailableOrdersParams.criteriaResolvers,
                    fulfillAvailableOrdersParams.offerFulfillments,
                    fulfillAvailableOrdersParams.considerationFulfillments,
                    fulfillAvailableOrdersParams.fulfillerConduitKey,
                    fulfillAvailableOrdersParams.recipient,
                    fulfillAvailableOrdersParams.maximumFulfilled
                ],
                value: BigInt(sum),
                chainId: paramChainId
            });

            return {
                value: transaction,
                sum
            };
        }
        catch (e) {
            const err = e as Error;
            console.warn(err.message);
            throw new Error('Error when making a purchase');
        }
    }
}
